import React from 'react';
import {
  Text, HStack, VStack, Icon,
} from '@gluestack-ui/themed';
import {
  FileText, AlignLeft, Eye, NotebookText, Search, Blocks, LucideIcon,
} from 'lucide-react-native';
import { Platform } from 'react-native';
import { useTenantGradingSettings } from '../../../../common/context/TenantSettingsStore';
import { TaskObjectivesDimensions } from '../../../../common/api/models/taskObjectivesDimensions';

// Icon mapping for different evaluation dimensions
const getDimensionIcon = (dimension: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    description: NotebookText,
    analysis: Search,
    application: Blocks,
    formal: AlignLeft,
    reflection: Eye,
  };
  return iconMap[dimension] || FileText;
};

interface TaskObjectivesProps {
  objectives: TaskObjectivesDimensions;
  variant?: 'compact' | 'comfortable';
  color?: string;
}

/**
 * Checks if the task has any applicable objectives/dimensions.
 * Useful for conditionally rendering headlines/containers.
 */
export const hasApplicableObjectives = (objectives?: TaskObjectivesDimensions): boolean => {
  if (!objectives) return false;
  return Object.values(objectives).some((dim) => dim.applicable === true);
};

const TaskObjectives: React.FC<TaskObjectivesProps> = ({
  objectives, variant = 'comfortable', color,
}) => {
  const { evalDimensionKeys, dimensionColors } = useTenantGradingSettings();

  const isEmpty = !objectives || Object.values(objectives).every((dim) => dim.applicable === false);

  const groupedObjectives = React.useMemo(() => {
    const availableDimensions = evalDimensionKeys.filter((dim) => objectives?.[dim]?.applicable);
    const groups: Record<string, string[]> = {};

    availableDimensions.forEach((dimension) => {
      groups[dimension] = objectives[dimension].demands;
    });

    return groups;
  }, [evalDimensionKeys, objectives]);

  if (isEmpty) return null;

  const isCompact = variant === 'compact';
  const labelSize = '2xs';
  const contentSize = isCompact ? (Platform.OS === 'web' ? 'xs' : 'sm') : 'sm';
  const iconSize = isCompact ? '2xs' : 'xs';
  const sectionSpace = isCompact ? 'xs' : 'sm';
  const outerSpace = isCompact ? 'xl' : 'xl';

  return (
    <VStack space={outerSpace}>
      {Object.entries(groupedObjectives).map(([dimension, demands]) => {
        const dimColor = dimensionColors[dimension] || `$${color}500`;
        const IconComponent = getDimensionIcon(dimension);

        return (
          <VStack key={dimension} space={sectionSpace}>
            <HStack space="xs" alignItems="center">
              <Icon as={IconComponent} size={iconSize} color={dimColor} />
              <Text size={labelSize} fontWeight="bold" color={dimColor} textTransform="uppercase" letterSpacing="$md">
                {dimension}
              </Text>
            </HStack>
            <VStack space="xs" pl={isCompact ? '$5' : '$6'}>
              {demands.map((demand) => (
                <HStack key={`${dimension}-demand-${demand}`} space="sm" alignItems="flex-start">
                  <Text size={contentSize} color="$textBody" lineHeight="$md" flex={1}>
                    {demand}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </VStack>
        );
      })}
    </VStack>
  );
};

export default TaskObjectives;
